import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Eye, CheckCircle } from 'lucide-react';
import { useGameStore } from '../store/gameStore';

interface ExerciseStep {
  id: number;
  instruction: string;
  direction: string;
  duration: number;
  icon: string;
}

const EXERCISES: ExerciseStep[] = [
  { id: 1, instruction: '眼球向上看', direction: 'up', duration: 3, icon: '⬆️' },
  { id: 2, instruction: '眼球向下看', direction: 'down', duration: 3, icon: '⬇️' },
  { id: 3, instruction: '眼球向左看', direction: 'left', duration: 3, icon: '⬅️' },
  { id: 4, instruction: '眼球向右看', direction: 'right', duration: 3, icon: '➡️' },
  { id: 5, instruction: '眼球顺时针转动', direction: 'clockwise', duration: 4, icon: '🔄' },
  { id: 6, instruction: '眼球逆时针转动', direction: 'counter-clockwise', duration: 4, icon: '🔃' },
  { id: 7, instruction: '快速眨眼', direction: 'blink', duration: 3, icon: '👁️' },
  { id: 8, instruction: '闭眼放松', direction: 'close', duration: 5, icon: '😌' },
  { id: 9, instruction: '远近焦点切换', direction: 'focus', duration: 4, icon: '🔍' },
  { id: 10, instruction: '上下左右交替看', direction: 'cross', duration: 4, icon: '✚' },
];

export default function EyeExercise() {
  const navigate = useNavigate();
  const { addEnergy, completeLevel } = useGameStore();
  const [currentStep, setCurrentStep] = useState(-1);
  const [gameState, setGameState] = useState<'ready' | 'playing' | 'complete'>('ready');
  const [countdown, setCountdown] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(0);

  const startExercise = () => {
    setGameState('playing');
    setCurrentStep(0);
    setCompletedSteps(0);
    setCountdown(EXERCISES[0].duration);
  };

  useEffect(() => {
    if (gameState !== 'playing' || currentStep < 0) return;

    if (countdown <= 0) {
      // Move to next step
      setCompletedSteps(c => c + 1);
      if (currentStep >= EXERCISES.length - 1) {
        setGameState('complete');
        return;
      }
      const next = currentStep + 1;
      setCurrentStep(next);
      setCountdown(EXERCISES[next].duration);
      return;
    }

    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, currentStep, gameState]);

  const handleComplete = () => {
    addEnergy(20);
    completeLevel({
      levelId: 'eye-exercise',
      levelName: '动态眼肌操',
      score: completedSteps * 10,
      accuracy: 100,
      reactionTime: 0,
      completedAt: new Date().toISOString(),
      contrast: 1,
      speed: 1,
    });
    navigate('/');
  };

  const getGuidePosition = (direction: string) => {
    switch (direction) {
      case 'up': return { x: 0, y: -80 };
      case 'down': return { x: 0, y: 80 };
      case 'left': return { x: -80, y: 0 };
      case 'right': return { x: 80, y: 0 };
      case 'clockwise': return { rotate: 360 };
      case 'counter-clockwise': return { rotate: -360 };
      case 'blink': return { scale: [1, 0.2, 1] };
      case 'close': return { opacity: 0.2 };
      case 'focus': return { scale: [1, 1.5, 0.8, 1] };
      case 'cross': return { x: [0, 0, -60, 60, 0], y: [-60, 60, 0, 0, 0] };
      default: return {};
    }
  };

  const exercise = currentStep >= 0 ? EXERCISES[currentStep] : null;

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#0a1a10] via-[#122a1a] to-[#1a3525] relative overflow-hidden">
      {/* Header */}
      <header className="relative z-10 px-4 pt-6 pb-2">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button onClick={() => navigate('/')} className="text-white/60 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold text-white">👁️ 动态眼肌操</h1>
          <div className="text-sm text-white/60">
            {completedSteps}/{EXERCISES.length}
          </div>
        </div>
        {gameState === 'playing' && (
          <div className="max-w-lg mx-auto mt-3">
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#4a9e6b] to-[#7dffa8] rounded-full"
                animate={{ width: `${(completedSteps / EXERCISES.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <div className="relative z-10 px-4 mt-8">
        <div className="max-w-lg mx-auto">
          {gameState === 'ready' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <Eye className="w-16 h-16 mx-auto mb-6 text-[#7dffa8]" />
              <h2 className="text-xl font-bold text-white mb-3">动态眼肌操</h2>
              <p className="text-white/60 text-sm mb-2 max-w-xs mx-auto">
                跟随屏幕上的引导点转动眼球。
              </p>
              <p className="text-white/60 text-sm mb-8 max-w-xs mx-auto">
                缓解视疲劳，增强眼外肌灵活性。共{EXERCISES.length}个动作。
              </p>
              <button onClick={startExercise} className="btn-primary text-lg">
                开始眼操 👁️
              </button>
            </motion.div>
          )}

          {gameState === 'playing' && exercise && (
            <div className="text-center">
              <motion.p
                key={exercise.id}
                className="text-white/80 text-lg font-medium mb-2"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {exercise.instruction}
              </motion.p>
              <p className="text-[#fbbf24] text-3xl font-bold mb-8">{countdown}</p>

              {/* Visual guide */}
              <div className="relative w-64 h-64 mx-auto rounded-full border-2 border-white/10 flex items-center justify-center bg-black/20">
                <motion.div
                  key={exercise.id}
                  className="w-12 h-12 rounded-full bg-[#7dffa8] flex items-center justify-center text-2xl"
                  style={{ boxShadow: '0 0 30px rgba(125,255,168,0.5)' }}
                  animate={getGuidePosition(exercise.direction)}
                  transition={{
                    duration: exercise.duration,
                    repeat: 0,
                    ease: 'easeInOut',
                  }}
                >
                  {exercise.icon}
                </motion.div>
                {/* Center reference dot */}
                <div className="absolute w-3 h-3 rounded-full bg-white/20" />
              </div>

              <p className="text-white/40 text-xs mt-6">跟随绿色引导点移动眼球</p>
            </div>
          )}

          {gameState === 'complete' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-16"
            >
              <CheckCircle className="w-16 h-16 mx-auto mb-6 text-[#7dffa8]" />
              <h2 className="text-xl font-bold text-white mb-3">眼操完成！</h2>
              <p className="text-white/60 text-sm mb-2">
                完成了全部 {EXERCISES.length} 个动作
              </p>
              <p className="text-[#fbbf24] text-sm mb-8">+20 能量</p>
              <button onClick={handleComplete} className="btn-primary text-lg">
                返回地图 🌲
              </button>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}
